import { useState, useEffect } from 'react';
import { HelpCircle, ExternalLink, X, Send, Instagram, Edit3, FileText, Video, Target } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  branding: '🎨',
  social_review: '📱',
  mix_presentation: '🎧',
  bio_optimization: '📝',
  content_strategy: '🎥'
};

export const ProfileAuditModal = ({ isOpen, onClose, onSuccess }) => {
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [socialLink, setSocialLink] = useState('');
  const [helpNeeded, setHelpNeeded] = useState('');
  const [goal, setGoal] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/community/audit-categories`);
        setCategories(response.data || {});
      } catch (error) {
        console.error('Failed to load audit categories');
      }
    };
    
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!selectedCategory || !socialLink || !helpNeeded || !goal) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/community/profile-audit`, {
        category: selectedCategory,
        social_link: socialLink,
        help_needed: helpNeeded,
        goal: goal
      });
      toast.success('Profile audit request posted!');
      onSuccess?.();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedCategory('');
    setSocialLink('');
    setHelpNeeded('');
    setGoal('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={handleClose}>
      <div 
        className="rounded-2xl p-6 border max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#1a1a1a', border: '1px solid rgba(212, 175, 55, 0.3)' }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <HelpCircle size={24} style={{ color: '#D4AF37' }} />
            <h3 className="text-lg font-bold">Request Profile Audit</h3>
          </div>
          <button onClick={handleClose}>
            <X size={20} />
          </button>
        </div>
        
        <p className="text-sm text-gray-400 mb-6">
          Get constructive feedback on your DJ branding from the community. Fill out the template below.
        </p>
        
        <div className="space-y-4">
          {/* Category Selection */}
          <div>
            <Label>What do you need help with? *</Label>
            <div className="grid grid-cols-1 gap-2 mt-2">
              {Object.entries(categories).map(([key, cat]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedCategory(key)}
                  className={`p-3 rounded-lg border text-left flex items-center gap-3 transition-all ${
                    selectedCategory === key 
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <span className="text-xl">{CATEGORY_ICONS[key] || '❓'}</span>
                  <div>
                    <p className="font-medium text-sm">{cat.name?.replace(/[🎨📱🎧📝🎥]\s?/, '')}</p>
                    <p className="text-xs text-gray-400">{cat.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Social Link */}
          <div>
            <Label>Link to your profile/content *</Label>
            <Input
              value={socialLink}
              onChange={(e) => setSocialLink(e.target.value)}
              placeholder="https://instagram.com/your-profile"
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Instagram, TikTok, SoundCloud, or any relevant link
            </p>
          </div>
          
          {/* What They Need Help With */}
          <div>
            <Label>What specifically would you like feedback on? *</Label>
            <Textarea
              value={helpNeeded}
              onChange={(e) => setHelpNeeded(e.target.value)}
              placeholder="e.g., I'm not sure if my bio is clear enough, or if my profile photo represents my DJ style..."
              rows={3}
              className="mt-1"
            />
          </div>
          
          {/* Goal */}
          <div>
            <Label>What's your goal? *</Label>
            <Textarea
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="e.g., I want to attract more club bookings, or grow my TikTok following..."
              rows={2}
              className="mt-1"
            />
          </div>
          
          <Button
            onClick={handleSubmit}
            disabled={loading || !selectedCategory || !socialLink || !helpNeeded || !goal}
            className="w-full rounded-full"
            style={{ background: '#D4AF37', color: '#000' }}
          >
            <Send size={16} className="mr-1" />
            {loading ? 'Posting...' : 'Post Audit Request'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Component to render a profile audit post with distinctive styling
export const ProfileAuditPost = ({ post, onReact, onComment, onExpandComments }) => {
  const categoryIcons = {
    branding: { icon: '🎨', color: '#f472b6' },
    social_review: { icon: '📱', color: '#818cf8' },
    mix_presentation: { icon: '🎧', color: '#22d3ee' },
    bio_optimization: { icon: '📝', color: '#4ade80' },
    content_strategy: { icon: '🎥', color: '#f97316' }
  };
  
  const category = categoryIcons[post.audit_category] || { icon: '❓', color: '#D4AF37' };
  
  return (
    <div 
      className="glass-panel rounded-2xl p-4 border transition-all"
      style={{ borderColor: `${category.color}40`, borderWidth: '2px' }}
      data-testid={`audit-post-${post.id}`}
    >
      {/* Category Tag */}
      <div 
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium mb-3"
        style={{ background: `${category.color}20`, color: category.color }}
      >
        <span>{category.icon}</span>
        <span>Profile Audit Request</span>
      </div>
      
      {/* Post Header */}
      <div className="flex items-center gap-3 mb-3">
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
          style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}
        >
          {(post.user_name || 'U').charAt(0)}
        </div>
        <div>
          <p className="font-semibold">{post.user_name}</p>
          <p className="text-xs text-gray-500">{new Date(post.created_at).toLocaleDateString()}</p>
        </div>
      </div>
      
      {/* Help Needed */}
      <div className="mb-3">
        <p className="text-sm font-semibold text-gray-400 mb-1">Needs feedback on:</p>
        <p className="text-gray-200">{post.help_needed}</p>
      </div>
      
      {/* Goal */}
      <div className="mb-3 p-3 rounded-lg" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
        <div className="flex items-center gap-2 mb-1">
          <Target size={14} style={{ color: '#D4AF37' }} />
          <p className="text-sm font-semibold" style={{ color: '#D4AF37' }}>Goal:</p>
        </div>
        <p className="text-sm text-gray-300">{post.goal}</p>
      </div>
      
      {/* Link */}
      {post.social_link && (
        <a 
          href={post.social_link} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm mb-3 hover:opacity-80 transition-opacity"
          style={{ background: category.color + '20', color: category.color }}
        >
          <ExternalLink size={14} />
          View Profile/Content
        </a>
      )}
    </div>
  );
};
